import React from "react";
import {PlaidEmbeddedLink, PlaidLinkOnExit, PlaidLinkOnSuccess} from "react-plaid-link";
import {SelectPaymentMethodWithWallet} from "@bounce/shared/consumer/pages/account-dashboard";
import {CardDetails} from "@bounce/shared/enums";
import {Box, Button, Colors, Container, LoadingCenter, Text} from "@bounce/shared/ui";

const EMBEDDED_MIN_HEIGHT = 475;

const NoCardMenu: React.FC<{cardDetails: CardDetails}> = () => null;

const SectionLoader = () => (
  <Container fullWidth sx={{minHeight: EMBEDDED_MIN_HEIGHT, position: "relative"}}>
    <LoadingCenter position="absolute" />
  </Container>
);

interface PlaidBankSectionProps {
  achBlocked: boolean;
  achMethods: CardDetails[];
  embeddedToken: string | null;
  hasMethods: boolean;
  selectedCardId: string | number | undefined;
  showEmbed: boolean;
  tokenError: boolean;
  walletLoaded: boolean;
  onAddPaymentMethod: () => void;
  onCancelConnect: () => void;
  onRetryConnect: () => void;
  onExit: PlaidLinkOnExit;
  onSuccess: PlaidLinkOnSuccess;
  setSelectedCardId: (id: string | number | undefined) => void;
}

const PlaidBankSection = ({
  achBlocked,
  achMethods,
  embeddedToken,
  hasMethods,
  selectedCardId,
  showEmbed,
  tokenError,
  walletLoaded,
  onAddPaymentMethod,
  onCancelConnect,
  onRetryConnect,
  onExit,
  onSuccess,
  setSelectedCardId,
}: PlaidBankSectionProps) => {
  if (!walletLoaded) {
    return <SectionLoader />;
  }

  if (!showEmbed) {
    return (
      <SelectPaymentMethodWithWallet
        achBlocked={achBlocked}
        selectedCardId={selectedCardId}
        hasPaymentMethod={hasMethods}
        allPaymentMethods={achMethods}
        onAdd={onAddPaymentMethod}
        setSelectedCardId={setSelectedCardId}
        CardMethodMenu={NoCardMenu}
      />
    );
  }

  if (tokenError) {
    return (
      <Container fullWidth sx={{alignItems: "start", gap: "8px"}}>
        <Text variant="inter400">Instant bank login isn't available right now.</Text>
        <Button buttonType="text" type="button" onClick={onRetryConnect}>
          Try again
        </Button>
      </Container>
    );
  }

  if (!embeddedToken) {
    return <SectionLoader />;
  }

  return (
    <Container fullWidth sx={{alignItems: "stretch", gap: "8px"}}>
      <Box
        sx={{
          border: `1px solid ${Colors.grey._400}`,
          borderRadius: "12px",
          overflow: "hidden",
          minHeight: EMBEDDED_MIN_HEIGHT,
          width: "100%",
        }}
      >
        <PlaidEmbeddedLink
          token={embeddedToken}
          onSuccess={onSuccess}
          onExit={onExit}
          style={{width: "100%", height: EMBEDDED_MIN_HEIGHT}}
        />
      </Box>
      {hasMethods && (
        <Button buttonType="text" type="button" onClick={onCancelConnect}>
          Cancel
        </Button>
      )}
    </Container>
  );
};

export default PlaidBankSection;
